import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import html2canvas from 'html2canvas';
import { generateQRTokenFromUser } from '../../services/qrTokenService';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import { useAuth } from '../../contexts/AuthContext';
import styles from './AssetQRModal.module.css';

const AssetQRModal = ({ asset, onClose }) => {
  const { currentUser } = useAuth();
  
  
  const isAssetDisposed = asset?.status === 'Disposed' || asset?.isDisposed === true;
  
  const [qrData, setQrData] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const BASE_URL = process.env.REACT_APP_BASE_URL || window.location.origin;
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'superadmin';

  const safeToDate = (timestamp) => {
    if (!timestamp) return null;
    if (typeof timestamp.toDate === 'function') return timestamp.toDate();
    if (timestamp instanceof Date) return timestamp;
    if (typeof timestamp === 'string') {
      const parsed = new Date(timestamp);
      return isNaN(parsed.getTime()) ? null : parsed;
    }
    if (typeof timestamp === 'number') return new Date(timestamp);
    return null;
  };

  const formatDate = (timestamp) => {
    const date = safeToDate(timestamp);
    if (!date) return 'Not specified';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const validateUserContext = () => {
    if (!currentUser) {
      setError('User authentication required. Please refresh and try again.');
      return false;
    }
    if (!currentUser.uid) {
      setError('User ID missing. Please log out and log back in.');
      return false;
    }
    if (!currentUser.companyId) {
      setError('Company information missing. Please contact support.');
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (!asset) {
      setIsLoading(false);
      return;
    }

    //  ADDED: Early check for disposed assets
    if (isAssetDisposed) {
      setError('QR code is not available for disposed assets.');
      setIsLoading(false);
      return;
    }

    const loadQRCode = async () => {
      try {
        setIsLoading(true);
        const existingToken = asset.qrToken;

        if (existingToken) {
          const assetUrl = `${BASE_URL}/public-asset/${asset.id}?token=${existingToken}`;
          const generatedAtDate = safeToDate(asset.qrCodeGeneratedAt);
          const generatedAtISO = generatedAtDate ? generatedAtDate.toISOString() : new Date().toISOString();
          setQrData({
            url: assetUrl,
            token: existingToken,
            generatedAt: generatedAtISO
          });
        } else {
          if (isAdmin) {
            await autoGenerateQRCode();
          } else {
            setError('QR code not available. Please contact your administrator.');
          }
        }
      } catch (err) {
        console.error('Error loading QR code:', err);
        setError(`Failed to load QR code: ${err.message}`);
      } finally {
        setIsLoading(false);
      }
    };

    const autoGenerateQRCode = async () => {
      try {
        if (!validateUserContext()) return;

        const token = await generateQRTokenFromUser(asset.id, currentUser);

        await updateDoc(doc(db, 'assets', asset.id), {
          qrToken: token,
          qrCodeGenerated: true,
          qrCodeGeneratedAt: new Date()
        });

        const assetUrl = `${BASE_URL}/public-asset/${asset.id}?token=${token}`;
        setQrData({
          url: assetUrl,
          token,
          generatedAt: new Date().toISOString()
        });
      } catch (err) {
        console.error('Auto QR generation failed:', err);
        setError(`Failed to auto-generate QR code: ${err.message}`);
      }
    };

    loadQRCode();
  }, [asset, BASE_URL, isAdmin, currentUser, isAssetDisposed]);

  const regenerateQRCode = async () => {
    
    if (isAssetDisposed) {
      setError('QR code cannot be regenerated for disposed assets.');
      return;
    }

    if (!isAdmin) {
      setError('Only administrators can regenerate QR codes.');
      return;
    }

    if (!validateUserContext()) return;

    try {
      setIsGenerating(true);
      setError(null);

      const token = await generateQRTokenFromUser(asset.id, currentUser);

      await updateDoc(doc(db, 'assets', asset.id), {
        qrToken: token,
        qrCodeGenerated: true,
        qrCodeGeneratedAt: new Date()
      });

      const assetUrl = `${BASE_URL}/public-asset/${asset.id}?token=${token}`;
      setQrData({
        url: assetUrl,
        token,
        generatedAt: new Date().toISOString()
      });
    } catch (err) {
      console.error('QR Code regeneration failed:', err);
      setError(`Failed to regenerate QR code: ${err.message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadQRCode = async () => {
    // ✅ ADDED: Prevent download if disposed
    if (isAssetDisposed) {
      setError('QR code cannot be downloaded for disposed assets.');
      return;
    }

    if (!qrData) return;

    try {
      setIsDownloading(true);
      const element = document.getElementById('qr-download-content');
      if (!element) throw new Error('QR content element not found');

      const canvas = await html2canvas(element, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        allowTaint: true,
        height: element.scrollHeight,
        width: element.scrollWidth,
        scrollX: 0,
        scrollY: 0
      });

      const downloadLink = document.createElement('a');
      downloadLink.href = canvas.toDataURL('image/png', 1.0);
      downloadLink.download = `asset-${asset.assetId || asset.id}-qrcode-complete.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    } catch (err) {
      console.error('Download failed:', err);
      setError(`Failed to download QR code: ${err.message}`);
    } finally {
      setIsDownloading(false);
    }
  };

  if (!asset) return null;

  if (isLoading) {
    return (
      <div className={styles.modalOverlay}>
        <div className={styles.modalContent}>
          <div className={styles.modalHeader}>
            <h3>QR Code for Asset</h3>
            <button className={styles.closeBtn} onClick={onClose}>×</button>
          </div>
          <div className={styles.modalBody}>
            <div className={styles.loadingContainer}>
              <div className={styles.spinner}></div>
              <p>Loading QR code...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ✅ ADDED: Show disposed asset warning
  if (isAssetDisposed) {
    return (
      <div className={styles.modalOverlay}>
        <div className={styles.modalContent}>
          <div className={styles.modalHeader}>
            <h3>QR Code Unavailable</h3>
            <button className={styles.closeBtn} onClick={onClose}>×</button>
          </div>
          <div className={styles.modalBody}>
            <div className={styles.errorMessage}>
              <div className={styles.errorIcon}>⚠️</div>
              <div>
                <p><strong>Asset Disposed:</strong> QR code is not available for disposed assets.</p>
                <p>Disposed assets are no longer tracked and their QR codes are deactivated.</p>
                <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
                  <strong>Asset Details:</strong>
                  <br />
                  <small>
                    <strong>Name:</strong> {asset.assetName}<br />
                    <strong>ID:</strong> {asset.assetId}<br />
                    <strong>Status:</strong> <span style={{ color: '#dc3545' }}>{asset.status}</span>
                    {asset.disposalDate && (
                      <>
                        <br />
                        <strong>Disposal Date:</strong> {asset.disposalDate.toDate ? asset.disposalDate.toDate().toLocaleDateString() : asset.disposalDate}
                      </>
                    )}
                  </small>
                </div>
              </div>
            </div>
          </div>
          <div className={styles.modalFooter}>
            <button className={styles.btnSecondary} onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (error && !qrData) {
    return (
      <div className={styles.modalOverlay}>
        <div className={styles.modalContent}>
          <div className={styles.modalHeader}>
            <h3>QR Code for Asset</h3>
            <button className={styles.closeBtn} onClick={onClose}>×</button>
          </div>
          <div className={styles.modalBody}>
            <div className={styles.errorMessage}>
              <div className={styles.errorIcon}>⚠️</div>
              <p>{error}</p>
              {!isAdmin && (
                <small>Only administrators can generate QR codes for assets.</small>
              )}
            </div>
          </div>
          <div className={styles.modalFooter}>
            <button className={styles.btnSecondary} onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <button className={styles.closeBtn} onClick={onClose}>×</button>
        </div>

        <div className={styles.modalBody}>
          {qrData && (
            <>
              <div id="qr-download-content" className={styles.downloadContent}>
                <div className={styles.logoContainer}>
                  <img
                    src="/logo.png"
                    alt="Company Logo"
                    className={styles.companyLogo}
                    onError={(e) => (e.target.style.display = 'none')}
                  />
                </div>

                <div className={styles.assetDetailsCard}>
                  <div className={styles.assetInfo}>
                    <h3 className={styles.assetTitle}>
                      {asset.name || asset.assetName}
                      {asset.brand && ` - ${asset.brand}`}
                      {asset.category && ` (${asset.category})`}
                    </h3>
                    
                    <div className={styles.assetDetailsList}>
                      <div className={styles.assetDetailItem}>
                        <strong>Asset ID:</strong> {asset.assetId}
                      </div>
                      
                      <div className={styles.assetDetailItem}>
                        <strong>Location:</strong>{' '}
                        {asset.location || asset.currentLocationName || 'Not specified'}
                      </div>

                      {asset.vendor && (
                        <div className={styles.assetDetailItem}>
                          <strong>Vendor:</strong> {asset.vendor}
                        </div>
                      )}

                      <div className={styles.assetDetailItem}>
                        <strong>Purchase Date:</strong> {formatDate(asset.purchaseDate || asset.dateOfPurchase)}
                      </div>

                      {(asset.expiryDate || asset.dateOfExpiry || asset.warrantyExpiry) && (
                        <div className={styles.assetDetailItem}>
                          <strong>Expiry Date:</strong> {formatDate(asset.expiryDate || asset.dateOfExpiry || asset.warrantyExpiry)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className={styles.qrCodeContainer}>
                  <QRCodeSVG
                    value={qrData.url}
                    size={160}          
                    includeMargin
                    level="H"
                  />
                  <div className={styles.qrCodeLabel}>
                    <p>Scan to view asset details</p>
                  </div>
                </div>

                <div className={styles.downloadFooter}>
                  <p>
                    {new Date(qrData.generatedAt).toLocaleDateString()} · Powered by
                    Assetra
                  </p>
                </div>
              </div>

              {error && (
                <div className={styles.errorMessage}>
                  <small>{error}</small>
                </div>
              )}
            </>
          )}
        </div>

        <div className={styles.modalFooter}>
          <button className={styles.btnSecondary} onClick={onClose}>
            Close
          </button>
          {qrData && !isAssetDisposed && ( 
            <button
              className={styles.btnDownload}
              onClick={downloadQRCode}
              disabled={isDownloading}
            >
              <i className="bi bi-download me-1" />
              {isDownloading ? 'Downloading...' : 'Download Complete QR'}
            </button>
          )}
          {isAdmin && !isAssetDisposed && ( 
            <button
              className={styles.btnGenerate}
              onClick={regenerateQRCode}
              disabled={isGenerating}
            >
              <i className="bi bi-arrow-repeat me-1" />
              {isGenerating ? 'Regenerating...' : 'Regenerate QR'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssetQRModal;